import { Controller } from "@hotwired/stimulus"

// El teléfono viaja como E.164 y el verificado llega como dígitos: se comparan por dígitos.
// Doc: _docs/features/stores/steps_form.md
function phoneDigits(value) {
  return String(value || "").replace(/\D/g, "")
}

//view create - edit stores
export default class extends Controller {
  static targets = ["step", "nextButton", "prevButton", "stepIndicator", "progressBar", "finishButton",
                    "phoneInput", "phoneStatus", "phoneVerifyButton"]
  static values = {
    createUrl: String,
    updateUrl: String,
    finishUrl: String,
    phoneVerificationUrl: String,
    verifiedPhone: String // número verificado por WhatsApp ("" si no hay)
  }

  connect() {
    this.currentStep = 0
    this.showCurrentStep()
    this.refreshPhoneStatus()
  }

  async next(event) {
    event.preventDefault()

    const isValid = await this.validateCurrentStep()
    if (!isValid) return

    // Paso 0: el teléfono se verifica por WhatsApp antes de avanzar (store-phone-modal:verified reanuda next())
    if (this.currentStep === 0 && this.hasPhoneInputTarget && !this.phoneVerified) {
      this.resumeAfterPhoneVerification = true
      await this.requestPhoneVerification()
      return
    }

    if (this.currentStep === 0 && !this.hasUpdateUrlValue) {
      // Tienda nueva: el paso 0 crea el borrador y captura las URLs de update_step / finish
      const created = await this.createDraft()
      if (!created) return
    } else if (this.hasUpdateUrlValue) {
      // Pasos siguientes (y toda la edición): autosave del paso actual.
      const saved = await this.saveCurrentStep()
      if (!saved) return
    }

    this.currentStep++
    this.showCurrentStep()
  }

  previous(event) {
    event.preventDefault()
    this.currentStep--
    this.showCurrentStep()
  }

  // Botón "Validar número con WhatsApp" junto al campo del paso 0.
  async verifyPhone(event) {
    event.preventDefault()
    if (!(await this.validateCurrentStep())) return

    this.resumeAfterPhoneVerification = false
    await this.requestPhoneVerification()
  }

  // La tienda debe existir para guardar el OTP: en creación se crea el borrador antes de abrir el modal
  async requestPhoneVerification() {
    if (!this.hasUpdateUrlValue && !(await this.createDraft())) return

    // El servidor anula la verificación anterior al pedir otro número: la marca local deja de valer
    this.verifiedPhoneValue = ""
    this.refreshPhoneStatus()
    this.phoneModal?.open({ url: this.phoneVerificationUrlValue, phone: this.phoneInputTarget.value })
  }

  get phoneModal() {
    const element = document.getElementById("store-phone-modal")
    return element && this.application.getControllerForElementAndIdentifier(element, "store-phone-modal")
  }

  // store-phone-modal:verified: marca el campo y, si venía de "Siguiente", continúa
  phoneVerificationCompleted(event) {
    this.verifiedPhoneValue = event.detail.phone
    this.refreshPhoneStatus()

    if (this.resumeAfterPhoneVerification) {
      this.resumeAfterPhoneVerification = false
      this.next({ preventDefault() {} })
    }
  }

  // ¿Lo escrito es el número verificado? Editarlo quita la marca hasta verificar de nuevo.
  get phoneVerified() {
    return this.verifiedPhoneValue !== "" &&
      phoneDigits(this.phoneInputTarget.value) === phoneDigits(this.verifiedPhoneValue)
  }

  refreshPhoneStatus() {
    if (!this.hasPhoneInputTarget) return

    const verified = this.phoneVerified
    if (this.hasPhoneStatusTarget) this.phoneStatusTarget.hidden = !verified
    if (this.hasPhoneVerifyButtonTarget) this.phoneVerifyButtonTarget.hidden = verified
  }

  // Botón final en edición: PATCH a #finish con un form efímero (los pasos ya se guardaron con update_step)
  finish(event) {
    event.preventDefault()
    if (!this.hasFinishUrlValue) return

    if (this.hasFinishButtonTarget) {
      this.finishButtonTarget.disabled = true
      this.finishButtonTarget.innerHTML =
        '<span class="spinner-border spinner-border-sm me-2"></span>Finalizando...'
    }

    const form = document.createElement("form")
    form.method = "post"
    form.action = this.finishUrlValue
    form.hidden = true

    const method = document.createElement("input")
    method.type = "hidden"
    method.name = "_method"
    method.value = "patch"
    form.appendChild(method)

    const csrf = document.createElement("input")
    csrf.type = "hidden"
    csrf.name = "authenticity_token"
    csrf.value = document.querySelector('meta[name="csrf-token"]')?.content || ""
    form.appendChild(csrf)

    document.body.appendChild(form)
    form.requestSubmit() // Turbo intercepta → maneja redirect (303) + flash
  }

  showCurrentStep() {
    // Mostrar solo el step actual, ocultar los demás
    this.stepTargets.forEach((el, index) => {
      el.hidden = index !== this.currentStep
    })

    // Actualizar indicadores visuales de pasos
    this.stepIndicatorTargets.forEach((el, index) => {
      el.classList.remove("active", "completed")

      if (index < this.currentStep) {
        el.classList.add("completed")  // pasos anteriores
      } else if (index === this.currentStep) {
        el.classList.add("active")     // paso actual
      }
    })

    // Actualizar barra de progreso
    if (this.hasProgressBarTarget) {
      const totalSteps = this.stepIndicatorTargets.length - 1
      const progress = totalSteps > 0 ? (this.currentStep / totalSteps) * 100 : 0
      this.progressBarTarget.style.width = `${progress}%`
    }

    // Controlar visibilidad de los botones
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = this.currentStep === 0
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.hidden = this.currentStep === this.stepTargets.length - 1
    }
  }

  // Tienda nueva: POST del paso 0 crea el borrador y captura las URLs de update_step / finish
  async createDraft() {
    const formData = this.buildStepFormData(0)
    this.setNextLoading(true)

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || "",
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        this.updateUrlValue = data.update_url
        this.finishUrlValue = data.finish_url
        this.phoneVerificationUrlValue = data.phone_verification_url
        this.element.dataset.storeId = data.store_id // unicidad de nombre (check_name)
        return true
      } else {
        this.showErrorMessage(data.errors)
        return false
      }
    } catch (error) {
      console.error('Error creating draft:', error)
      this.showErrorMessage(['Error de conexión. Intenta de nuevo.'])
      return false
    } finally {
      this.setNextLoading(false)
    }
  }

  async saveCurrentStep() {
    const formData = this.buildStepFormData(this.currentStep)
    this.setNextLoading(true)

    try {
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || "",
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        return true
      } else {
        this.showErrorMessage(data.errors)
        return false
      }
    } catch (error) {
      console.error('Error saving step:', error)
      this.showErrorMessage(['Error de conexión. Intenta de nuevo.'])
      return false
    } finally {
      this.setNextLoading(false)
    }
  }

  // Recoge los inputs de un paso en un FormData (incluye el índice de paso).
  buildStepFormData(stepIndex) {
    const stepEl = this.stepTargets[stepIndex]
    const formData = new FormData()

    const inputs = stepEl.querySelectorAll("input, select, textarea")
    inputs.forEach(input => {
      if (!input.name) return

      if (input.type === 'checkbox') {
        formData.append(input.name, input.checked ? '1' : '0')
      } else if (input.type === 'select-multiple') {
        Array.from(input.selectedOptions).forEach(option => {
          formData.append(input.name, option.value)
        })
      } else if (input.type === 'file') {
        // Solo enviar si hay archivos nuevos seleccionados
        if (input.files.length > 0) {
          Array.from(input.files).forEach(file => {
            formData.append(input.name, file)
          })
        }
      } else {
        formData.append(input.name, input.value)
      }
    })

    formData.append('step', stepIndex)
    return formData
  }

  setNextLoading(loading) {
    if (!this.hasNextButtonTarget) return

    this.nextButtonTarget.disabled = loading
    this.nextButtonTarget.innerHTML = loading
      ? '<span class="spinner-border spinner-border-sm me-2"></span>Guardando...'
      : 'Siguiente <i class="bi bi-arrow-right"></i>'
  }

  showErrorMessage(errors) {
    const toast = document.createElement('div')
    toast.className = 'position-fixed bottom-0 end-0 p-3'
    toast.style.zIndex = '9999'
    toast.innerHTML = `
      <div class="toast show align-items-center text-white bg-danger border-0" role="alert">
        <div class="d-flex">
          <div class="toast-body">
            <i class="bi bi-exclamation-circle me-2"></i>${errors.join(', ')}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
        </div>
      </div>
    `
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 5000)
  }


  async validateCurrentStep() {
    const currentStep = this.stepTargets[this.currentStep]
    const inputs = currentStep.querySelectorAll("input, select, textarea")
    let valid = true

    inputs.forEach(input => {
      if (input.required) {
        const isMultiSelect = input.tagName === "SELECT" && input.multiple
        const isFileInput = input.type === "file"
        const hasAttachment = input.dataset.hasAttachment === "true"

        const isEmpty =
          isMultiSelect
            ? input.selectedOptions.length === 0
            : isFileInput
              ? input.files.length === 0 && !hasAttachment
              : !input.value.trim()

        if (isEmpty) {
          input.classList.add("is-invalid")
          this.addOrUpdateFeedback(input, "Este campo es obligatorio.")
          valid = false
        } else {
          input.classList.remove("is-invalid")
          this.removeFeedback(input)
        }
      }
    })


    // Horarios de tienda: un solape marcado por store-schedule también bloquea el paso.
    if (currentStep.querySelector(".hours-shift.is-invalid, .hours-all-day.is-invalid")) valid = false

    const storeId = this.element.dataset.storeId || ""
    // Validación AJAX de unicidad del nombre solo en el paso 0
    if (this.currentStep === 0) {
      const nameInput = currentStep.querySelector('input[name="store[name]"]')
      if (nameInput && nameInput.value.trim() !== "") {
        const normalizedName = nameInput.value.trim().toLowerCase()
        const response = await fetch(`/stores/check_name?name=${encodeURIComponent(normalizedName)}&current_id=${storeId}`, {
          headers: { 'Accept': 'application/json' }
        })
        const data = await response.json()


        if (!data.available) {
          nameInput.classList.add("is-invalid")
          this.addOrUpdateFeedback(nameInput, "Este nombre ya está en uso.")
          valid = false
        } else {
          nameInput.classList.remove("is-invalid")
          this.removeFeedback(nameInput)
        }
      }
    }

    return valid
  }

  // El hidden `required` de una foto (photo-field) deja el aviso dentro de su campo
  addOrUpdateFeedback(input, message) {
    let container =
      input.closest(".photo-field") ||
      input.closest(".form-group") ||
      input.parentNode

    let feedback = container.querySelector(".invalid-feedback")

    if (!feedback) {
      feedback = document.createElement("div")
      feedback.classList.add("invalid-feedback", "d-block") // fuerza visibilidad
      container.appendChild(feedback)
    }

    feedback.textContent = message
  }

  removeFeedback(input) {
    const feedback = input.parentNode.querySelector(".invalid-feedback")
    if (feedback) feedback.remove()
  }


}
